// ClientPortfolio — Addepar-inspired portfolio view for a single client.
// Three sub-views:
//   - Overview:    KPI strip, allocation donut, drift bars, top holdings, accounts.
//   - Holdings:    flat sortable/filterable table across every account.
//   - Performance: time-series chart (when valuations exist) + period returns.
//
// Plus modals for: new account, new holding, CSV import, target allocation editor.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ArrowRight, BarChart3, Briefcase, Building2, Calculator, ChevronUp, ChevronDown,
  Download, FileSpreadsheet, Filter, Layers, LineChart as LineChartIcon,
  Loader2, Pencil, Plus, RefreshCw, Sliders, Trash2, Upload, Wallet, X,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  LineChart, Line, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import logger from '@/lib/logger';
import AccountFormModal from '@/components/portfolio/AccountFormModal';
import HoldingFormModal from '@/components/portfolio/HoldingFormModal';
import ImportPortfolioDialog from '@/components/portfolio/ImportPortfolioDialog';
import TargetAllocationModal from '@/components/portfolio/TargetAllocationModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALLOC_COLORS = {
  Cash: '#0EA5E9',
  'Australian Equities': '#16A34A',
  'International Equities': '#9333EA',
  'Fixed Income': '#D97706',
  Property: '#DB2777',
  Alternatives: '#0891B2',
  'Private Markets': '#7C3AED',
  Unclassified: '#94A3B8',
};

const ALLOC_FALLBACK = ['#4A3B32', '#A86A3A', '#D97706', '#16A34A', '#0EA5E9', '#9333EA', '#DB2777'];

function fmtMoney(n, opts = {}) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (opts.compact && abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (opts.compact && abs >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n);
}
function fmtPct(n, sign = false) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const s = `${n.toFixed(1)}%`;
  return sign && n > 0 ? `+${s}` : s;
}
function colorForClass(name, idx) {
  return ALLOC_COLORS[name] || ALLOC_FALLBACK[idx % ALLOC_FALLBACK.length];
}

function KpiCard({ label, value, sub, accent, testId }) {
  return (
    <div className="rounded-xl border border-[#E6E0D9] bg-white p-4" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wide text-[#8C8078]">{label}</p>
      <p className={cn('text-2xl font-heading mt-1', accent || 'text-[#2C2420]')}>{value}</p>
      {sub && <p className="text-xs text-[#5D524D] mt-1">{sub}</p>}
    </div>
  );
}

export default function ClientPortfolio() {
  const { clientId } = useParams();
  const { token } = useAuth();
  const [view, setView] = useState('overview'); // overview | holdings | performance
  const [summary, setSummary] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showHoldingModal, setShowHoldingModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showTarget, setShowTarget] = useState(false);
  const [holdingDefaults, setHoldingDefaults] = useState({});
  const [holdingInitial, setHoldingInitial] = useState(null);
  const [accountInitial, setAccountInitial] = useState(null);
  const [sort, setSort] = useState({ field: 'marketValue', dir: 'desc' });
  const [filterClass, setFilterClass] = useState('all');
  const [filterAccount, setFilterAccount] = useState('all');

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    if (!token || !clientId) return;
    setLoading(true);
    try {
      const [sumRes, holdRes] = await Promise.all([
        fetch(`${API}/clients/${clientId}/portfolio/summary`, { headers }),
        fetch(`${API}/clients/${clientId}/holdings`, { headers }),
      ]);
      if (sumRes.ok) setSummary(await sumRes.json());
      if (holdRes.ok) setHoldings((await holdRes.json()).items || []);
    } catch (e) {
      logger.warn('ClientPortfolio: load failed', e);
      toast.error('Could not load portfolio data');
    } finally {
      setLoading(false);
    }
  }, [token, clientId, headers]);

  useEffect(() => { load(); }, [load]);

  // Sort + filter holdings
  const filteredSorted = useMemo(() => {
    let rows = [...holdings];
    if (filterClass !== 'all') rows = rows.filter((h) => h.assetClass === filterClass);
    if (filterAccount !== 'all') rows = rows.filter((h) => h.accountId === filterAccount);
    const { field, dir } = sort;
    rows.sort((a, b) => {
      const av = a[field] ?? '';
      const bv = b[field] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return dir === 'asc' ? av - bv : bv - av;
      return dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return rows;
  }, [holdings, sort, filterClass, filterAccount]);

  const toggleSort = (field) => {
    setSort((s) => s.field === field ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'desc' });
  };

  const accountsById = useMemo(() => {
    const m = {};
    (summary?.accounts || []).forEach((a) => { m[a.id] = a; });
    return m;
  }, [summary]);

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API}/portfolio/template`, { headers });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'portfolio_import_template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not download template');
    }
  };

  const exportHoldingsCsv = () => {
    if (!holdings.length) { toast.info('No holdings to export'); return; }
    const cols = ['account_name', 'security_name', 'ticker', 'asset_class', 'sector', 'region', 'currency', 'quantity', 'unit_price', 'market_value', 'cost_base', 'unrealised_gain', 'as_of_date'];
    const accountsMap = {};
    (summary?.accounts || []).forEach((a) => { accountsMap[a.id] = a.name; });
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = holdings.map((h) => [
      accountsMap[h.accountId] || '', h.securityName, h.ticker, h.assetClass, h.sector, h.region,
      h.currency, h.quantity, h.unitPrice, h.marketValue, h.costBase, h.unrealisedGain, h.asOfDate,
    ].map(escape).join(','));
    const csv = [cols.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `holdings_${clientId}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const deleteHolding = async (h) => {
    if (!window.confirm(`Delete holding "${h.securityName}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/holdings/${h.id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error(await res.text());
      toast.success('Holding deleted');
      load();
    } catch (e) {
      logger.warn('deleteHolding failed', e);
      toast.error('Could not delete holding');
    }
  };

  const deleteAccount = async (a) => {
    if (!window.confirm(`Delete account "${a.name}"? All holdings on this account will also be removed.`)) return;
    try {
      const res = await fetch(`${API}/investment-accounts/${a.id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error(await res.text());
      toast.success('Account deleted');
      load();
    } catch (e) {
      logger.warn('deleteAccount failed', e);
      toast.error('Could not delete account');
    }
  };

  const editHolding = (h) => { setHoldingInitial(h); setHoldingDefaults({}); setShowHoldingModal(true); };
  const editAccount = (a) => { setAccountInitial(a); setShowAccountModal(true); };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="portfolio-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#4A3B32]" />
      </div>
    );
  }

  const totals = summary?.totals || {};
  const totalValue = totals.marketValue || 0;
  const unrealised = totals.unrealisedGain || 0;
  const unrealisedPct = totals.costBase ? (unrealised / totals.costBase) * 100 : null;
  const isEmpty = (totals.holdingCount || 0) === 0 && (totals.accountCount || 0) === 0;

  return (
    <div className="space-y-6" data-testid="client-portfolio">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading text-[#2C2420] flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#A86A3A]" />
            Portfolio
          </h1>
          <p className="text-sm text-[#5D524D]">
            {summary?.lastUpdated ? `Holdings as of ${summary.lastUpdated}` : 'No holdings on file yet.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 justify-end" data-testid="portfolio-actions">
          <Button variant="outline" size="sm" onClick={load} data-testid="portfolio-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="portfolio-template-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Template
          </Button>
          <Button variant="outline" size="sm" onClick={exportHoldingsCsv} data-testid="portfolio-export-btn">
            <Download className="w-4 h-4 mr-1.5" /> Export
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowImport(true)} data-testid="portfolio-import-btn">
            <Upload className="w-4 h-4 mr-1.5" /> Import CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowTarget(true)} data-testid="portfolio-target-btn">
            <Sliders className="w-4 h-4 mr-1.5" /> Target allocation
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setAccountInitial(null); setShowAccountModal(true); }} data-testid="portfolio-new-account-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Account
          </Button>
          <Button size="sm" onClick={() => { setHoldingInitial(null); setHoldingDefaults({}); setShowHoldingModal(true); }} className="bg-[#4A3B32] hover:bg-[#3A2E26] text-white" data-testid="portfolio-new-holding-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Holding
          </Button>
        </div>
      </div>

      {isEmpty ? (
        <EmptyState
          onImport={() => setShowImport(true)}
          onAccount={() => setShowAccountModal(true)}
          onTemplate={downloadTemplate}
        />
      ) : (
        <>
          {/* Sub-nav */}
          <div className="flex items-center gap-1 border-b border-[#E6E0D9]" data-testid="portfolio-sub-nav">
            {[
              { id: 'overview', label: 'Overview', icon: Layers },
              { id: 'holdings', label: 'Holdings', icon: Wallet },
              { id: 'performance', label: 'Performance', icon: LineChartIcon },
            ].map((t) => {
              const Icon = t.icon;
              const active = view === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setView(t.id)}
                  className={cn(
                    'flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 -mb-px transition-colors',
                    active
                      ? 'border-[#4A3B32] text-[#2C2420] font-medium'
                      : 'border-transparent text-[#5D524D] hover:text-[#2C2420]'
                  )}
                  data-testid={`portfolio-tab-${t.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                </button>
              );
            })}
          </div>

          {view === 'overview' && (
            <OverviewView
              summary={summary}
              totals={totals}
              totalValue={totalValue}
              unrealised={unrealised}
              unrealisedPct={unrealisedPct}
              onOpenTarget={() => setShowTarget(true)}
              onAddHolding={(acctId) => { setHoldingInitial(null); setHoldingDefaults({ accountId: acctId }); setShowHoldingModal(true); }}
              onEditAccount={editAccount}
              onDeleteAccount={deleteAccount}
            />
          )}

          {view === 'holdings' && (
            <HoldingsView
              holdings={filteredSorted}
              total={totalValue}
              accountsById={accountsById}
              accounts={summary?.accounts || []}
              filterClass={filterClass}
              setFilterClass={setFilterClass}
              filterAccount={filterAccount}
              setFilterAccount={setFilterAccount}
              sort={sort}
              toggleSort={toggleSort}
              onEdit={editHolding}
              onDelete={deleteHolding}
            />
          )}

          {view === 'performance' && (
            <PerformanceView performance={summary?.performance} />
          )}
        </>
      )}

      {/* Modals */}
      {showAccountModal && (
        <AccountFormModal
          clientId={clientId}
          initial={accountInitial || {}}
          onClose={() => { setShowAccountModal(false); setAccountInitial(null); }}
          onSaved={() => { setShowAccountModal(false); setAccountInitial(null); load(); toast.success('Account saved'); }}
        />
      )}
      {showHoldingModal && (
        <HoldingFormModal
          clientId={clientId}
          accounts={summary?.accounts || []}
          defaults={holdingDefaults}
          initial={holdingInitial || {}}
          onClose={() => { setShowHoldingModal(false); setHoldingInitial(null); }}
          onSaved={() => { setShowHoldingModal(false); setHoldingInitial(null); load(); toast.success('Holding saved'); }}
        />
      )}
      {showImport && (
        <ImportPortfolioDialog
          clientId={clientId}
          onClose={() => setShowImport(false)}
          onImported={(result) => {
            setShowImport(false);
            load();
            toast.success(`Imported ${result.holdingsCreated} holdings across ${result.accountsTouched} account${result.accountsTouched === 1 ? '' : 's'}.`);
          }}
          onTemplate={downloadTemplate}
        />
      )}
      {showTarget && (
        <TargetAllocationModal
          clientId={clientId}
          onClose={() => setShowTarget(false)}
          onSaved={() => { setShowTarget(false); load(); toast.success('Target allocation updated'); }}
        />
      )}
    </div>
  );
}

function EmptyState({ onImport, onAccount, onTemplate }) {
  return (
    <div className="rounded-xl border border-dashed border-[#E6E0D9] p-12 text-center" data-testid="portfolio-empty">
      <BarChart3 className="w-12 h-12 mx-auto text-[#D4CCC6] mb-3" />
      <h2 className="font-heading text-xl text-[#2C2420] mb-1">No portfolio data yet</h2>
      <p className="text-sm text-[#5D524D] max-w-md mx-auto mb-6">
        Import a positions spreadsheet, or add accounts and holdings manually to start building this client's portfolio view.
      </p>
      <div className="flex items-center justify-center gap-2 flex-wrap">
        <Button onClick={onImport} className="bg-[#4A3B32] hover:bg-[#3A2E26] text-white" data-testid="portfolio-empty-import">
          <Upload className="w-4 h-4 mr-1.5" /> Import CSV
        </Button>
        <Button variant="outline" onClick={onTemplate} data-testid="portfolio-empty-template">
          <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Download template
        </Button>
        <Button variant="outline" onClick={onAccount} data-testid="portfolio-empty-account">
          <Plus className="w-4 h-4 mr-1.5" /> Add account
        </Button>
      </div>
    </div>
  );
}

function OverviewView({ summary, totals, totalValue, unrealised, unrealisedPct, onOpenTarget, onAddHolding, onEditAccount, onDeleteAccount }) {
  const perf = summary?.performance || {};
  return (
    <div className="space-y-6">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="portfolio-kpi-strip">
        <KpiCard label="Total Value"     value={fmtMoney(totalValue, { compact: true })} sub={fmtMoney(totalValue)} testId="kpi-total" />
        <KpiCard
          label="Unrealised G/L"
          value={fmtMoney(unrealised, { compact: true })}
          sub={unrealisedPct === null ? null : fmtPct(unrealisedPct, true)}
          accent={unrealised >= 0 ? 'text-green-700' : 'text-red-600'}
          testId="kpi-unrealised"
        />
        <KpiCard label="Cash"            value={fmtMoney(totals.cashValue || 0, { compact: true })} sub={`${totalValue > 0 ? ((totals.cashValue || 0) / totalValue * 100).toFixed(1) : 0}% of portfolio`} testId="kpi-cash" />
        <KpiCard label="Accounts"        value={totals.accountCount || 0} testId="kpi-accounts" />
        <KpiCard label="Holdings"        value={totals.holdingCount || 0} testId="kpi-holdings" />
        <KpiCard label="YTD Return"      value={perf.ytd === null || perf.ytd === undefined ? '—' : fmtPct(perf.ytd, true)} sub="Time-weighted" accent={(perf.ytd || 0) >= 0 ? 'text-green-700' : 'text-red-600'} testId="kpi-ytd" />
      </div>

      {/* Allocation donut + Drift bars */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AllocationDonut allocation={summary?.allocation || []} totalValue={totalValue} />
        <DriftPanel drift={summary?.drift || []} onOpenTarget={onOpenTarget} />
      </div>

      {/* Top holdings */}
      <TopHoldings rows={summary?.topHoldings || []} totalValue={totalValue} />

      {/* Accounts table */}
      <AccountsSummary accounts={summary?.accounts || []} totalValue={totalValue} onAddHolding={onAddHolding} onEdit={onEditAccount} onDelete={onDeleteAccount} />
    </div>
  );
}

function AllocationDonut({ allocation, totalValue }) {
  const data = allocation.map((row, idx) => ({
    ...row,
    fill: colorForClass(row.assetClass, idx),
  }));
  return (
    <div className="rounded-xl border border-[#E6E0D9] bg-white p-5" data-testid="allocation-donut">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-base text-[#2C2420]">Asset allocation</h3>
        <span className="text-xs text-[#8C8078]">{data.length} classes</span>
      </div>
      {data.length === 0 ? (
        <p className="text-sm text-[#8C8078] py-10 text-center">No holdings allocated yet.</p>
      ) : (
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="w-44 h-44">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="marketValue" nameKey="assetClass" innerRadius="60%" outerRadius="100%" paddingAngle={2}>
                  {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtMoney(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="flex-1 space-y-1.5 min-w-0" data-testid="allocation-legend">
            {data.map((d) => (
              <li key={d.assetClass} className="flex items-center justify-between gap-3 text-sm">
                <span className="flex items-center gap-2 min-w-0">
                  <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: d.fill }} />
                  <span className="text-[#2C2420] truncate">{d.assetClass}</span>
                </span>
                <span className="text-[#5D524D] tabular-nums shrink-0">{d.percent.toFixed(1)}%</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-xs text-[#8C8078] mt-3">Portfolio total: {fmtMoney(totalValue)}</p>
    </div>
  );
}

function DriftPanel({ drift, onOpenTarget }) {
  return (
    <div className="rounded-xl border border-[#E6E0D9] bg-white p-5" data-testid="drift-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-base text-[#2C2420]">Drift vs target</h3>
        <Button variant="ghost" size="sm" onClick={onOpenTarget} className="h-7 text-xs text-[#A86A3A]" data-testid="drift-edit-btn">
          <Sliders className="w-3.5 h-3.5 mr-1" /> Edit targets
        </Button>
      </div>
      {!drift.length || drift.every((r) => r.targetPercent === null) ? (
        <div className="py-10 text-center" data-testid="drift-empty">
          <p className="text-sm text-[#5D524D] mb-3">No target allocation set.</p>
          <Button variant="outline" size="sm" onClick={onOpenTarget}>Set targets</Button>
        </div>
      ) : (
        <ul className="space-y-3">
          {drift.map((row) => {
            const max = Math.max(row.currentPercent, row.targetPercent || 0, 5);
            const cw = (row.currentPercent / max) * 100;
            const tw = ((row.targetPercent || 0) / max) * 100;
            const dr = row.driftPercent ?? 0;
            const drClass = Math.abs(dr) < 2 ? 'text-[#5D524D]' : dr > 0 ? 'text-orange-600' : 'text-blue-600';
            return (
              <li key={row.assetClass} className="text-sm" data-testid={`drift-row-${row.assetClass.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[#2C2420] font-medium truncate">{row.assetClass}</span>
                  <span className={cn('text-xs tabular-nums', drClass)}>
                    {row.targetPercent === null ? 'No target' : (
                      <>{fmtPct(row.currentPercent)} vs {fmtPct(row.targetPercent)} <span className="text-[#8C8078]">({fmtPct(dr, true)}pp)</span></>
                    )}
                  </span>
                </div>
                <div className="relative h-2 bg-[#F5F3F1] rounded-full overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-[#4A3B32]/60" style={{ width: `${Math.min(cw, 100)}%` }} />
                  {row.targetPercent !== null && (
                    <div
                      className="absolute top-[-2px] bottom-[-2px] w-[2px] bg-[#A86A3A]"
                      style={{ left: `${Math.min(tw, 100)}%` }}
                      title={`Target ${fmtPct(row.targetPercent)}`}
                    />
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function TopHoldings({ rows, totalValue }) {
  return (
    <div className="rounded-xl border border-[#E6E0D9] bg-white" data-testid="top-holdings">
      <div className="px-5 py-3 border-b border-[#E6E0D9] flex items-center justify-between">
        <h3 className="font-heading text-base text-[#2C2420]">Top holdings</h3>
        <span className="text-xs text-[#8C8078]">Top {Math.min(rows.length, 10)}</span>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-[#8C8078] py-8 text-center">No holdings to rank yet.</p>
      ) : (
        <div className="divide-y divide-[#F0EAE3]">
          {rows.map((h, i) => (
            <div key={h.id} className="px-5 py-3 grid grid-cols-12 gap-3 items-center" data-testid={`top-holding-${i}`}>
              <span className="col-span-1 text-xs text-[#8C8078] tabular-nums">#{i + 1}</span>
              <div className="col-span-5 min-w-0">
                <p className="text-sm font-medium text-[#2C2420] truncate">{h.securityName}</p>
                <p className="text-xs text-[#8C8078]">{h.ticker || '—'} · {h.assetClass || 'Unclassified'}</p>
              </div>
              <div className="col-span-3">
                <div className="h-1.5 bg-[#F5F3F1] rounded-full overflow-hidden">
                  <div className="h-full bg-[#A86A3A]" style={{ width: `${Math.min(h.weight, 100)}%` }} />
                </div>
              </div>
              <span className="col-span-1 text-xs text-[#5D524D] tabular-nums text-right">{h.weight.toFixed(1)}%</span>
              <span className="col-span-2 text-sm text-[#2C2420] tabular-nums text-right">{fmtMoney(h.marketValue, { compact: true })}</span>
            </div>
          ))}
        </div>
      )}
      <p className="px-5 py-2 text-[11px] text-[#8C8078]">Total portfolio: {fmtMoney(totalValue)}</p>
    </div>
  );
}

function AccountsSummary({ accounts, totalValue, onAddHolding, onEdit, onDelete }) {
  return (
    <div className="rounded-xl border border-[#E6E0D9] bg-white" data-testid="accounts-summary">
      <div className="px-5 py-3 border-b border-[#E6E0D9] flex items-center justify-between">
        <h3 className="font-heading text-base text-[#2C2420]">Accounts</h3>
        <span className="text-xs text-[#8C8078]">{accounts.length} account{accounts.length === 1 ? '' : 's'}</span>
      </div>
      {accounts.length === 0 ? (
        <p className="text-sm text-[#8C8078] py-8 text-center">No accounts yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs text-[#8C8078] uppercase tracking-wide">
            <tr>
              <th className="text-left px-5 py-2 font-normal">Account</th>
              <th className="text-left px-5 py-2 font-normal hidden md:table-cell">Custodian</th>
              <th className="text-left px-5 py-2 font-normal hidden lg:table-cell">Type</th>
              <th className="text-right px-5 py-2 font-normal">Market value</th>
              <th className="text-right px-5 py-2 font-normal">Weight</th>
              <th className="px-5 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F0EAE3]">
            {accounts.map((a) => {
              const weight = totalValue > 0 ? (a.marketValue / totalValue) * 100 : 0;
              return (
                <tr key={a.id} data-testid={`account-row-${a.id}`}>
                  <td className="px-5 py-3">
                    <p className="font-medium text-[#2C2420]">{a.name}</p>
                  </td>
                  <td className="px-5 py-3 text-[#5D524D] hidden md:table-cell">{a.custodian || '—'}</td>
                  <td className="px-5 py-3 text-[#5D524D] hidden lg:table-cell">{a.accountType || '—'}</td>
                  <td className="px-5 py-3 text-right text-[#2C2420] tabular-nums">{fmtMoney(a.marketValue)}</td>
                  <td className="px-5 py-3 text-right text-[#5D524D] tabular-nums">{weight.toFixed(1)}%</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => onAddHolding(a.id)} className="text-xs h-7" data-testid={`account-add-holding-${a.id}`}>
                        <Plus className="w-3 h-3 mr-1" /> Holding
                      </Button>
                      {onEdit && (
                        <Button variant="ghost" size="icon" onClick={() => onEdit(a)} className="h-7 w-7 text-[#5D524D] hover:text-[#2C2420]" data-testid={`account-edit-${a.id}`} title="Edit account">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {onDelete && (
                        <Button variant="ghost" size="icon" onClick={() => onDelete(a)} className="h-7 w-7 text-[#5D524D] hover:text-red-600" data-testid={`account-delete-${a.id}`} title="Delete account">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function HoldingsView({ holdings, total, accountsById, accounts, filterClass, setFilterClass, filterAccount, setFilterAccount, sort, toggleSort, onEdit, onDelete }) {
  const classes = Array.from(new Set(holdings.map((h) => h.assetClass).filter(Boolean))).sort();
  const Sort = ({ field, label, align = 'left' }) => (
    <th className={cn('px-3 py-2 font-normal text-xs uppercase tracking-wide text-[#8C8078] cursor-pointer select-none', align === 'right' && 'text-right')}>
      <button onClick={() => toggleSort(field)} className="inline-flex items-center gap-1 hover:text-[#2C2420]" data-testid={`holdings-sort-${field}`}>
        {label}
        {sort.field === field && (sort.dir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
      </button>
    </th>
  );
  return (
    <div className="rounded-xl border border-[#E6E0D9] bg-white overflow-hidden" data-testid="holdings-view">
      {/* Filters */}
      <div className="px-5 py-3 border-b border-[#E6E0D9] flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 text-sm text-[#5D524D]">
          <Filter className="w-4 h-4" /> Filter:
        </div>
        <select value={filterAccount} onChange={(e) => setFilterAccount(e.target.value)} className="text-sm border border-[#E6E0D9] rounded-md px-2 py-1" data-testid="filter-account">
          <option value="all">All accounts</option>
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select value={filterClass} onChange={(e) => setFilterClass(e.target.value)} className="text-sm border border-[#E6E0D9] rounded-md px-2 py-1" data-testid="filter-class">
          <option value="all">All asset classes</option>
          {classes.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="text-xs text-[#8C8078] ml-auto">{holdings.length} holding{holdings.length === 1 ? '' : 's'}</span>
      </div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-[#FCFAF7]">
            <tr>
              <Sort field="securityName" label="Security" />
              <Sort field="ticker" label="Ticker" />
              <Sort field="assetClass" label="Class" />
              <th className="px-3 py-2 font-normal text-xs uppercase tracking-wide text-[#8C8078]">Account</th>
              <Sort field="quantity" label="Qty" align="right" />
              <Sort field="marketValue" label="Market value" align="right" />
              <Sort field="costBase" label="Cost base" align="right" />
              <Sort field="unrealisedGain" label="Unrealised" align="right" />
              <th className="px-3 py-2 font-normal text-xs uppercase tracking-wide text-[#8C8078] text-right">Weight</th>
              <th className="px-3 py-2 font-normal text-xs uppercase tracking-wide text-[#8C8078] text-right w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F0EAE3]">
            {holdings.map((h) => {
              const weight = total > 0 ? (h.marketValue / total) * 100 : 0;
              const gain = h.unrealisedGain || 0;
              return (
                <tr key={h.id} className="hover:bg-[#FCFAF7] group" data-testid={`holding-row-${h.id}`}>
                  <td className="px-3 py-2 font-medium text-[#2C2420]">{h.securityName}</td>
                  <td className="px-3 py-2 text-[#5D524D]">{h.ticker || '—'}</td>
                  <td className="px-3 py-2 text-[#5D524D]">{h.assetClass || '—'}</td>
                  <td className="px-3 py-2 text-[#5D524D] truncate max-w-[180px]">{accountsById[h.accountId]?.name || '—'}</td>
                  <td className="px-3 py-2 text-right text-[#5D524D] tabular-nums">{Number(h.quantity || 0).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right text-[#2C2420] tabular-nums">{fmtMoney(h.marketValue)}</td>
                  <td className="px-3 py-2 text-right text-[#5D524D] tabular-nums">{fmtMoney(h.costBase)}</td>
                  <td className={cn('px-3 py-2 text-right tabular-nums', gain >= 0 ? 'text-green-700' : 'text-red-600')}>{fmtMoney(gain)}</td>
                  <td className="px-3 py-2 text-right text-[#5D524D] tabular-nums">{weight.toFixed(1)}%</td>
                  <td className="px-2 py-2 text-right">
                    <div className="inline-flex items-center opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                      {onEdit && (
                        <button onClick={() => onEdit(h)} className="p-1 text-[#5D524D] hover:text-[#2C2420]" data-testid={`holding-edit-${h.id}`} title="Edit holding">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {onDelete && (
                        <button onClick={() => onDelete(h)} className="p-1 text-[#5D524D] hover:text-red-600" data-testid={`holding-delete-${h.id}`} title="Delete holding">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {holdings.length === 0 && (
              <tr><td colSpan={10} className="px-3 py-10 text-center text-sm text-[#8C8078]">No holdings match the current filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PerformanceView({ performance }) {
  const series = performance?.series || [];
  if (!series.length) {
    return (
      <div className="rounded-xl border border-[#E6E0D9] bg-white p-10 text-center" data-testid="performance-empty">
        <LineChartIcon className="w-10 h-10 text-[#D4CCC6] mx-auto mb-3" />
        <p className="text-sm text-[#5D524D]">No valuations on file yet.</p>
        <p className="text-xs text-[#8C8078] mt-1">Add a month-end account total via the API to start tracking performance over time.</p>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="performance-view">
      <div className="rounded-xl border border-[#E6E0D9] bg-white p-5">
        <h3 className="font-heading text-base text-[#2C2420] mb-3">Total value over time</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="#F0EAE3" />
              <XAxis dataKey="periodEnd" stroke="#8C8078" fontSize={11} />
              <YAxis stroke="#8C8078" fontSize={11} tickFormatter={(v) => fmtMoney(v, { compact: true })} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Line type="monotone" dataKey="totalValue" stroke="#A86A3A" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="YTD"   value={performance?.ytd === null || performance?.ytd === undefined ? '—' : fmtPct(performance.ytd, true)} testId="perf-ytd" />
        <KpiCard label="1 year" value={performance?.oneYear === null || performance?.oneYear === undefined ? '—' : fmtPct(performance.oneYear, true)} testId="perf-1y" />
        <KpiCard label="MTD"   value="—" sub="Requires daily data" testId="perf-mtd" />
        <KpiCard label="QTD"   value="—" sub="Requires monthly data" testId="perf-qtd" />
      </div>
    </div>
  );
}
